/**
 * Base module state hook.
 *
 * Provides shared functionality for exam module state management including
 * session ID generation, answer submission preparation, result calculation,
 * and saving answers/results to the backend.
 *
 * This hook eliminates code duplication between reading and listening modules
 * by extracting common patterns into a reusable base hook.
 *
 * @module useModuleStateBase
 */

import { useRef, useCallback, useEffect } from 'react';
import { saveUserAnswersBatch, saveSimulationResult } from '@/lib/examApi';
import { generateSessionId } from '@/utils/sessionUtils';
import { assertAnswerSubmissionsComplete } from '@/utils/moduleAnswerSubmission';
import { PASSING_SCORE_PERCENTAGE } from '@/lib/constants';
import { requireValue, handleApiErrorStandardized } from '@/lib/errorHandling';
import { logger } from '@/lib/logger';
import type {
  CEFRLevel,
  ExamType,
  ExamModule,
  SimulationSettings,
  Question,
  UserAnswerSubmission,
  SimulationResultSubmission,
} from '@/types/exam-simulation';

/**
 * Options for the base module state hook.
 */
interface UseModuleStateBaseOptions {
  /** Exam module name (e.g., 'reading', 'listening') */
  module: ExamModule;
  /** Exam type (goethe, osd, or telc) */
  examType: ExamType;
  /** CEFR proficiency level (A1, A2, B1, B2, C1, or C2) */
  level: CEFRLevel;
  /** Simulation settings from API (null if not yet loaded) */
  settings: SimulationSettings | null;
  /** Current user ID (optional, used for session tracking) */
  userId?: string | null;
  /** Whether to calculate correctness and points in answer submission (default: false) */
  calculateCorrectness?: boolean;
  /** Session ID passed from parent (if provided, skips local generation) */
  sessionId?: string | null;
}

/**
 * Return value from the base module state hook.
 */
interface UseModuleStateBaseReturn {
  /** Generated session ID (null if userId not provided) */
  sessionId: string | null;
  /** Function to prepare answers for submission */
  prepareAnswersForSubmission: (
    questions: Question[],
    selectedAnswers: Record<number, string>,
    changedQuestionIds?: Record<number, true>,
  ) => UserAnswerSubmission[];
  /** Function to prepare result for submission */
  prepareResultForSubmission: (
    questions: Question[],
    selectedAnswers: Record<number, string>,
  ) => SimulationResultSubmission | null;
  /** Function to save answers and result to backend */
  saveAnswersAndResult: (
    questions: Question[],
    selectedAnswers: Record<number, string>,
    changedQuestionIds?: Record<number, true>,
  ) => Promise<void>;
}

/**
 * Base hook for managing common exam module state functionality.
 *
 * This hook consolidates shared functionality across exam modules (reading, listening, etc.)
 * while maintaining clear separation of concerns:
 *
 * **Session Management:**
 * - Generates and maintains a unique session ID for tracking user exam sessions
 * - Session ID is created once per component mount and persists for the session lifetime
 *
 * **Answer Preparation:**
 * - Transforms frontend question/answer format to backend submission format
 * - Handles both answered and unanswered questions
 * - Optionally calculates correctness and points (for modules that support it)
 * - Maps answer IDs to answer text for submission
 *
 * **Result Calculation:**
 * - Calculates exam score based on expected answers
 * - Determines pass/fail status using configurable passing threshold
 * - Prepares result data structure for backend submission
 * - Only calculates when `calculateCorrectness` is enabled
 *
 * **Data Persistence:**
 * - Saves user answers in batch to backend
 * - Saves exam result to backend
 * - Handles errors gracefully with standardized error handling
 * - Provides logging for debugging and monitoring
 *
 * **Design Rationale:**
 * While this hook handles multiple concerns, they are all related to the core
 * responsibility of exam module state management. Splitting further would:
 * - Add unnecessary complexity (multiple hooks to import and coordinate)
 * - Reduce cohesion (related functionality scattered across files)
 * - Increase coupling (hooks would need to share state/refs)
 *
 * The current structure provides a good balance between separation of concerns
 * and practical maintainability.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing session ID and submission functions
 *
 * @example
 * ```typescript
 * const {
 *   sessionId,
 *   prepareAnswersForSubmission,
 *   prepareResultForSubmission,
 *   saveAnswersAndResult,
 * } = useModuleStateBase({
 *   module: 'reading',
 *   examType: 'goethe',
 *   level: 'B1',
 *   settings: simulationSettings,
 *   userId: user?.id,
 *   calculateCorrectness: true,
 * });
 * ```
 */
/**
 * Normalizes answer text for storage and comparison.
 * For true-false questions, uses the ID ('true'/'false').
 * For multiple choice, uses the option text.
 */
const getQuestionAnswerText = (
  question: Question,
  selectedAnswerId: string | null | undefined,
): string | null => {
  if (selectedAnswerId === undefined || selectedAnswerId === null) {
    return null;
  }

  const selectedOption = question.options?.find((opt) => opt.id === selectedAnswerId);
  if (!selectedOption) {
    return selectedAnswerId;
  }

  const isTrueFalse = selectedOption.id === 'true' || selectedOption.id === 'false';
  return isTrueFalse ? selectedOption.id : selectedOption.text;
};

/**
 * Checks if a selected answer is correct.
 * Only returns true if the answer exists and matches the expected answer.
 */
const isAnswerCorrect = (
  question: Question,
  selectedAnswerId: string | null | undefined,
): boolean => {
  if (!question.expectedAnswer || selectedAnswerId === undefined || selectedAnswerId === null) {
    return false;
  }

  const isTrueFalse = selectedAnswerId === 'true' || selectedAnswerId === 'false';
  const normalizedCorrect = question.expectedAnswer.toLowerCase().trim();

  if (isTrueFalse) {
    return selectedAnswerId.toLowerCase() === normalizedCorrect;
  }

  const selectedOption = question.options?.find((opt) => opt.id === selectedAnswerId);
  if (!selectedOption) {
    return false;
  }

  return selectedOption.text.toLowerCase().trim() === normalizedCorrect;
};

export function useModuleStateBase({
  module,
  examType,
  level,
  settings,
  userId,
  calculateCorrectness = false,
  sessionId: externalSessionId,
}: UseModuleStateBaseOptions): UseModuleStateBaseReturn {
  // Use external session ID if provided, otherwise generate locally (for backward compatibility)
  const sessionIdRef = useRef<string | null>(externalSessionId || null);
  if (!sessionIdRef.current && userId) {
    sessionIdRef.current = generateSessionId(userId);
  }

  useEffect(() => {
    if (externalSessionId) {
      sessionIdRef.current = externalSessionId;
    }
  }, [externalSessionId]);

  /**
   * Prepare all answers for submission (answered and unanswered)
   */
  const prepareAnswersForSubmission = useCallback(
    (
      questions: Question[],
      selectedAnswers: Record<number, string>,
      changedQuestionIds?: Record<number, true>,
    ): UserAnswerSubmission[] => {
      const sessionId = sessionIdRef.current;
      if (!userId || !sessionId) {
        return [];
      }

      return questions.map((question) => {
        const selectedAnswerId = selectedAnswers[question.id];
        const isAnswered = selectedAnswerId !== undefined && selectedAnswerId !== null;

        const answerText = getQuestionAnswerText(question, selectedAnswerId);
        let isCorrect: boolean | null = null;
        let points: number | null = null;

        if (calculateCorrectness) {
          if (!isAnswered) {
            // Unanswered questions are marked as incorrect when grading is enabled
            isCorrect = false;
            points = 0;
          } else if (question.expectedAnswer) {
            // Calculate correctness and points only if question has a expected answer defined
            isCorrect = isAnswerCorrect(question, selectedAnswerId);
            points = isCorrect ? 1 : 0;
          }
        }

        return {
          user_id: userId,
          session_id: sessionId,
          question_id: question.originalId,
          module,
          section: question.section || 1,
          answer_text: answerText,
          audio_data: null,
          audio_format: null,
          audio_duration_seconds: null,
          is_correct: isCorrect,
          points: points,
          // `question.id` is the 1..N position the learner saw (after any
          // per-session shuffle / resume reorder), so the results/review page
          // can replay the exact same order.
          display_order: question.id,
          was_changed: Boolean(changedQuestionIds?.[question.id]),
        };
      });
    },
    [userId, module, calculateCorrectness],
  );

  /**
   * Calculate score and prepare result submission
   */
  const prepareResultForSubmission = useCallback(
    (
      questions: Question[],
      selectedAnswers: Record<number, string>,
    ): SimulationResultSubmission | null => {
      const sessionId = sessionIdRef.current;
      if (!userId || !sessionId || !settings) {
        return null;
      }

      // Calculate score using helper function for consistent logic
      const correctCount = questions.filter((q) => isAnswerCorrect(q, selectedAnswers[q.id]))
        .length;

      const totalQuestions = questions.length;
      const score = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;
      const passed = score >= PASSING_SCORE_PERCENTAGE;

      return {
        session_id: sessionId,
        user_id: userId,
        is_success: true,
        passed,
        score,
        notes: null,
        level,
        is_graded: true,
        module,
        exam_type: examType,
      };
    },
    [userId, level, examType, settings, module],
  );

  /**
   * Save all answers and result to database
   */
  const saveAnswersAndResult = useCallback(
    async (
      questions: Question[],
      selectedAnswers: Record<number, string>,
      changedQuestionIds?: Record<number, true>,
    ): Promise<void> => {
      // Validate required values with standardized error handling
      requireValue(userId, 'User ID is required', 'saveAnswersAndResult');
      requireValue(sessionIdRef.current, 'Session ID is required', 'saveAnswersAndResult');

      // Prepare all answers (answered and unanswered)
      const answerSubmissions = prepareAnswersForSubmission(
        questions,
        selectedAnswers,
        changedQuestionIds,
      );

      if (questions.length > 0) {
        try {
          assertAnswerSubmissionsComplete(questions, answerSubmissions);
        } catch (error) {
          logger.error('Answer submission preparation incomplete', error, {
            event: 'simulation.answers.prepare_incomplete',
            module,
            sessionId: sessionIdRef.current,
            userId,
            questionCount: questions.length,
            submissionCount: answerSubmissions.length,
          });
          handleApiErrorStandardized(
            error,
            'Failed to prepare answers for submission',
            'saveAnswersAndResult',
          );
          throw error;
        }

        try {
          logger.event(
            'simulation.answers.prepare_and_save.started',
            {
              module,
              sessionId: sessionIdRef.current,
              userId,
              answerCount: answerSubmissions.length,
              questionIds: answerSubmissions.map((answer) => answer.question_id),
            },
            'info',
            'Module answer save started',
          );
          await saveUserAnswersBatch({ answers: answerSubmissions });
          logger.event(
            'simulation.answers.prepare_and_save.succeeded',
            {
              module,
              sessionId: sessionIdRef.current,
              userId,
              answerCount: answerSubmissions.length,
            },
            'info',
            'Module answer save succeeded',
          );
        } catch (error) {
          logger.error('Module answer save failed', error, {
            event: 'simulation.answers.prepare_and_save.failed',
            module,
            sessionId: sessionIdRef.current,
            userId,
          });
          handleApiErrorStandardized(
            error,
            'Failed to save user answers batch',
            'saveAnswersAndResult',
          );
          throw error;
        }
      }

      // Prepare and save result
      const resultSubmission = prepareResultForSubmission(questions, selectedAnswers);
      if (resultSubmission) {
        try {
          logger.event(
            'simulation.result.prepare_and_save.started',
            {
              module,
              sessionId: resultSubmission.session_id,
              userId,
              score: resultSubmission.score,
              passed: resultSubmission.passed,
            },
            'info',
            'Module result save started',
          );
          await saveSimulationResult(resultSubmission);
          logger.event(
            'simulation.result.prepare_and_save.succeeded',
            {
              module,
              sessionId: resultSubmission.session_id,
              userId,
              score: resultSubmission.score,
              passed: resultSubmission.passed,
            },
            'info',
            'Module result save succeeded',
          );
        } catch (error) {
          logger.error('Module result save failed', error, {
            event: 'simulation.result.prepare_and_save.failed',
            module,
            sessionId: resultSubmission.session_id,
            userId,
          });
          handleApiErrorStandardized(
            error,
            'Failed to save simulation result',
            'saveAnswersAndResult',
          );
          throw error;
        }
      }
    },
    [prepareAnswersForSubmission, prepareResultForSubmission, userId],
  );

  return {
    sessionId: sessionIdRef.current,
    prepareAnswersForSubmission,
    prepareResultForSubmission,
    saveAnswersAndResult,
  };
}
